import {
  type ArrayPath,
  type Control,
  type FieldValues,
  useFieldArray,
} from 'react-hook-form';
import ConditionalContactAgeRestrictionForm from './conditional-contact-age-restriction-form';
import ExclusionPeriodForm from './exclusion-period-form';
import FormulaBasedRestrictionForm from './formula-based-restriction-form';
import FrequencyLimitForm from './frequency-limit-form';
import LifeEventLimitForm from './life-event-limit-form';
import StepSizeLimitForm from './step-size-limit-form';
import TotalLimitForm from './total-limit-form';
import { type RestrictionType } from './types';

export const RESTRICTION_CONFIG: Record<
  RestrictionType,
  {
    component: React.ElementType;
    title: string;
    defaultValues: Record<string, unknown>;
    needsSetValue?: boolean;
    needsTrigger?: boolean;
  }
> = {
  frequencyLimits: {
    component: FrequencyLimitForm,
    title: 'FREQUENCY LIMITS',
    defaultValues: {
      maxChanges: undefined,
      limitType: 'change',
      periodType: 'policy_year',
      customPeriodStart: undefined,
      customPeriodEnd: undefined,
    },
    needsSetValue: true,
  },
  stepSizeLimits: {
    component: StepSizeLimitForm,
    title: 'STEP SIZE LIMITS',
    defaultValues: {
      maxStepSize: undefined,
      limitType: 'change',
      periodType: 'policy_year',
      customPeriodStart: undefined,
      customPeriodEnd: undefined,
    },
    needsSetValue: true,
  },
  totalLimits: {
    component: TotalLimitForm,
    title: 'TOTAL LIMITS',
    defaultValues: {
      maxAmount: undefined,
      limitType: 'change',
      periodType: 'policy_year',
      customPeriodStart: undefined,
      customPeriodEnd: undefined,
    },
    needsSetValue: true,
  },
  lifeEventLimits: {
    component: LifeEventLimitForm,
    title: 'LIFE EVENT LIMITS',
    defaultValues: {
      maxLifeEventChanges: undefined,
    },
  },
  exclusionPeriods: {
    component: ExclusionPeriodForm,
    title: 'EXCLUSION PERIODS',
    defaultValues: {
      startTrigger: 'specific_date',
      startTriggerSpecificDate: undefined,
      durationDays: null,
      ignoreRestrictions: false,
      ignoreWindows: false,
    },
    needsSetValue: true,
  },
  formulaBasedRestrictions: {
    component: FormulaBasedRestrictionForm,
    title: 'FORMULA BASED RESTRICTIONS',
    defaultValues: {
      formula: '',
      message: '',
    },
  },
  conditionalContactAgeRestrictions: {
    component: ConditionalContactAgeRestrictionForm,
    title: 'CONDITIONAL CONTACT AGE RESTRICTIONS',
    defaultValues: {
      contactType: 'CHILD',
      condition: '',
      priority: 0,
      minAge: undefined,
      maxAge: undefined,
    },
    needsTrigger: true,
  },
};

export const useRestrictionFieldArrays = <TFieldValues extends FieldValues>(
  control: Control<TFieldValues>,
  restrictionSetPath: string
) => {
  const getRestrictionArrayPath = (
    restrictionType: RestrictionType
  ): ArrayPath<TFieldValues> =>
    `${restrictionSetPath}.${restrictionType}` as ArrayPath<TFieldValues>;

  const frequencyLimits = useFieldArray({
    control,
    name: getRestrictionArrayPath('frequencyLimits'),
  });

  const stepSizeLimits = useFieldArray({
    control,
    name: getRestrictionArrayPath('stepSizeLimits'),
  });

  const totalLimits = useFieldArray({
    control,
    name: getRestrictionArrayPath('totalLimits'),
  });

  const lifeEventLimits = useFieldArray({
    control,
    name: getRestrictionArrayPath('lifeEventLimits'),
  });

  const exclusionPeriods = useFieldArray({
    control,
    name: getRestrictionArrayPath('exclusionPeriods'),
  });

  const formulaBasedRestrictions = useFieldArray({
    control,
    name: getRestrictionArrayPath('formulaBasedRestrictions'),
  });

  const conditionalContactAgeRestrictions = useFieldArray({
    control,
    name: getRestrictionArrayPath('conditionalContactAgeRestrictions'),
  });

  return {
    frequencyLimits,
    stepSizeLimits,
    totalLimits,
    lifeEventLimits,
    exclusionPeriods,
    formulaBasedRestrictions,
    conditionalContactAgeRestrictions,
  };
};
